import { IDocumentUnscoped } from '../../../types/my-types';
import {
  DocumentDataExclusionReasons,
  DocumentDataStatusFilters,
} from '../../../types/packager-types';
import { formatExclusionReason } from '../../../types/packager-enum-descriptions';

/**
 * Split-action helpers. Nothing here refuses a split: exclusion is a decision about
 * DELIVERY, not about whether a file can be carved, and the backend stays authoritative on
 * submit. These describe the document so the UI can warn before acting.
 */

type ProcessingFields = Pick<IDocumentUnscoped, 'document_data_status'>;
type AlreadySplitFields = Pick<IDocumentUnscoped, 'relationships'>;
type SplitEligibilityFields = Pick<
  IDocumentUnscoped,
  'document_data_status' | 'exclusion_reason' | 'relationships'
>;

/** The document's data is still being created (initial processing). */
export function isDocumentProcessing(doc: ProcessingFields): boolean {
  return doc.document_data_status === DocumentDataStatusFilters.PENDING_CREATION;
}

/**
 * The document has already been split. Reads ``relationships.split``, never the
 * ``has_children`` flag — that flag is true for a parent in ANY relationship
 * type, so the canonical document of a duplicate set would look already-split.
 */
export function isDocumentAlreadySplit(doc: AlreadySplitFields): boolean {
  return (doc.relationships?.split?.children?.length ?? 0) > 0;
}

/**
 * A consequence to show before splitting — never a refusal. Splitting an excluded
 * document effectively undoes the exclusion: its children are created un-excluded and
 * deliver on their own, which is invisible from the split screen.
 *
 * Keyed on whether a split actually happened, not on the reason string. CONCATENATED is
 * the previous split's own footprint and needs no warning, but the reason alone does not
 * prove a split: rows hand-set to EXCLUDED/CONCATENATED before that was withheld from the
 * status editors carry the reason with no children, and exempting those is how the one
 * case this warning exists for ends up silent. Reason-independent also means an EXCLUDED
 * row with no reason still warns.
 */
export function splitCaution(doc: SplitEligibilityFields): string | null {
  if (
    doc.document_data_status !== DocumentDataStatusFilters.EXCLUDED ||
    isDocumentAlreadySplit(doc)
  ) {
    return null;
  }
  const why = doc.exclusion_reason
    ? ` (${formatExclusionReason(doc.exclusion_reason as DocumentDataExclusionReasons)})`
    : '';
  return (
    `This document is excluded${why}. Splitting it creates children that are ` +
    `NOT excluded and will be delivered on their own.`
  );
}
